import { useState } from "react";
import { useAuthStore } from "./auth-store";

type Mode = "signin" | "signup" | "confirm";

export function AuthModal() {
  const { showModal, loading, error, signIn, signUp, confirmSignUp, closeModal, clearError } = useAuthStore();
  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [pendingEmail, setPendingEmail] = useState("");

  if (!showModal) return null;

  async function handleSignIn(e: React.FormEvent) {
    e.preventDefault();
    try {
      await signIn(email, password);
    } catch {}
  }

  async function handleSignUp(e: React.FormEvent) {
    e.preventDefault();
    try {
      const result = await signUp(email, password);
      if (result.needsConfirmation) {
        setPendingEmail(email);
        setMode("confirm");
      }
    } catch {}
  }

  async function handleConfirm(e: React.FormEvent) {
    e.preventDefault();
    try {
      await confirmSignUp(pendingEmail, code);
      await signIn(pendingEmail, password);
    } catch {}
  }

  function switchMode(m: Mode) {
    clearError();
    setMode(m);
  }

  return (
    <div className="fixed inset-0 bg-black/80 z-[1000] flex items-center justify-center p-4" onClick={closeModal}>
      <div
        className="bg-th-surface border border-th-border rounded-xl p-6 w-full max-w-md shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-bold">
            {mode === "signin" ? "Sign In" : mode === "signup" ? "Create Account" : "Verify Email"}
          </h2>
          <button
            className="w-8 h-8 rounded-full bg-th-btn border border-th-border text-th-text-2 hover:bg-red-500 hover:text-white transition-colors"
            onClick={closeModal}
          >
            ✕
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
            {error}
          </div>
        )}

        {mode === "confirm" ? (
          <form onSubmit={handleConfirm} className="space-y-4">
            <p className="text-th-text-2 text-sm">
              We sent a verification code to <strong>{pendingEmail}</strong>.
            </p>
            <input
              type="text"
              placeholder="Verification code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="w-full bg-th-input border border-th-border rounded-lg px-3 py-2 text-th-text text-sm"
              autoFocus
            />
            <button
              type="submit"
              disabled={loading || !code}
              className="w-full py-2.5 bg-glossika-600 hover:bg-glossika-500 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
            >
              {loading ? "Verifying..." : "Verify"}
            </button>
          </form>
        ) : (
          <form onSubmit={mode === "signin" ? handleSignIn : handleSignUp} className="space-y-4">
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-th-input border border-th-border rounded-lg px-3 py-2 text-th-text text-sm"
              autoFocus
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-th-input border border-th-border rounded-lg px-3 py-2 text-th-text text-sm"
            />
            <button
              type="submit"
              disabled={loading || !email || !password}
              className="w-full py-2.5 bg-glossika-600 hover:bg-glossika-500 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
            >
              {loading
                ? mode === "signin" ? "Signing in..." : "Creating account..."
                : mode === "signin" ? "Sign In" : "Create Account"}
            </button>
            <div className="text-center text-sm text-th-text-3">
              {mode === "signin" ? (
                <>
                  No account?{" "}
                  <button type="button" className="text-glossika-400 hover:underline" onClick={() => switchMode("signup")}>
                    Create one
                  </button>
                </>
              ) : (
                <>
                  Already have an account?{" "}
                  <button type="button" className="text-glossika-400 hover:underline" onClick={() => switchMode("signin")}>
                    Sign in
                  </button>
                </>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
